import React, { useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { parseISO, isBefore } from 'date-fns';

import { queryClient } from '@api/queryClient';
import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button/button';
import { Icon } from '@epoch-ui/components/Icon';
import { SheetHeader } from '@epoch-ui/components/Sheet/sheet';
import { QUESTION_TYPE, Question } from 'types/question.types';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import {
  QUESTION_CONFIG,
  QuestionFormInput,
  QuestionFormValues
} from '@event-editor/components/EditQuestionSideSheet/config/questionConfig';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { addEventFeedbackQuestion } from '@event-editor/api/feedback/addEventFeedbackQuestion';
import { updateEventFeedbackQuestion } from '@event-editor/api/feedback/updateEventFeedbackQuestion';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { getEditEventFeedbackData } from '@event-editor/api/feedback/getEditEventFeedbackData';
import { DeleteQuestionConfirmationDialog } from '@event-editor/components/EditQuestionSideSheet/DeleteQuestionConfirmationDialog';

interface FeedbackQuestionEditorProps {
  type: QUESTION_TYPE;
  onBack: () => void;
  handleClose: () => void;
  question?: Question;
  onWarningResendFeedback: () => void;
  disabled?: boolean;
}

export const FeedbackQuestionEditor: React.FC<FeedbackQuestionEditorProps> = ({
  type,
  onBack,
  handleClose,
  question,
  onWarningResendFeedback,
  disabled
}) => {
  const event = useEditableEvent();
  const { showToast } = useToaster();

  // Access the cached feedback data using the same queryKey from the EditEventFeedback component
  // This won't trigger a new network request
  const { data: feedbackData } = useQuery({
    queryKey: eventEditorQueryKeys.eventFeedback(Number(event.id)),
    queryFn: () => getEditEventFeedbackData(Number(event.id)),
    enabled: typeof Number(event.id) === 'number' && Number(event.id) > 0
  });

  const feedbackSubmitted = feedbackData?.feedbackStats?.feedbackSubmitted ?? 0;

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

  const questionsLength = event.postQuestions?.filter((q) => !q.isDeleted).length || 0;

  // Get the configuration for this question type
  const {
    defaultValues,
    schema,
    component: QuestionComponent,
    convertValuesFromDB
  } = useMemo(() => {
    const conf = QUESTION_CONFIG[type];
    if (!conf) {
      throw new Error(`Unsupported question type: ${type}`);
    }
    return conf;
  }, [type]);

  // Get the initial values by merging defaults with input question
  const initialValues = useMemo<QuestionFormInput>(() => {
    // Start with default values
    let values = defaultValues;

    // If we have a question, merge it with defaults
    if (question) {
      values = {
        ...defaultValues,
        ...question,
        // Ensure type is set correctly
        type
      } as QuestionFormValues;
    }

    // Convert to form format if needed (even for default values)
    if (convertValuesFromDB) {
      return convertValuesFromDB(values);
    }

    return values;
  }, [defaultValues, convertValuesFromDB, question, type]);

  // Initialize the form
  const methods = useForm<QuestionFormInput, unknown, QuestionFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: initialValues
  });

  const {
    handleSubmit,
    formState: { isValid, isSubmitting }
  } = methods;

  const updateQuestionMutation = useMutation({
    mutationFn: (variables: { eventId: number; question: Question }) =>
      updateEventFeedbackQuestion({
        eventId: variables.eventId,
        question: {
          id: variables.question.id,
          label: variables.question.label,
          type: variables.question.type,
          options: variables.question.options,
          hasOtherOption: variables.question.hasOtherOption,
          shownBefore: false,
          isRequired: variables.question.isRequired ?? false,
          properties: variables.question.properties,
          isDeleted: variables.question.isDeleted ?? false
        }
      }),
    onError: () => {
      showToast('Failed to update question', { variant: ToasterVariant.ERROR });
    },
    onSuccess: (result, variables) => {
      const { event: eventResult, question: updatedQuestion } = result.updateEventFeedbackQuestion;

      queryClient.setQueryData(eventEditorQueryKeys.event(variables.eventId), (oldData: any) =>
        oldData
          ? {
              event: {
                ...oldData.event,
                postQuestions: oldData.event.postQuestions.map((q: Question) =>
                  q.id === updatedQuestion.id ? updatedQuestion : q
                ),
                feedbackEnable: eventResult.feedbackEnable,
                isFeedbackAnonymous: eventResult.isFeedbackAnonymous,
                feedbackSchedule: eventResult.feedbackSchedule,
                feedbackNotificationChannel: eventResult.feedbackNotificationChannel
              }
            }
          : oldData
      );

      // Warning if feedback form has been sent
      const sendAt = eventResult.feedbackSchedule?.sendAt;
      if (sendAt && isBefore(parseISO(sendAt), new Date())) {
        onWarningResendFeedback();
      }

      handleClose();
    }
  });

  const addQuestionMutation = useMutation({
    mutationFn: (variables: { eventId: number; question: Question }) =>
      addEventFeedbackQuestion({
        eventId: variables.eventId,
        question: {
          ...variables.question,
          sort: questionsLength,
          shownBefore: false
        }
      }),
    onError: () => {
      showToast('Failed to add question', { variant: ToasterVariant.ERROR });
    },
    onSuccess: (result, variables) => {
      const { question: addedQuestion, event: eventResult } = result.addEventFeedbackQuestion;

      queryClient.setQueryData(eventEditorQueryKeys.event(variables.eventId), (oldData: any) =>
        oldData
          ? {
              event: {
                ...oldData.event,
                postQuestions: [...oldData.event.postQuestions, addedQuestion],
                feedbackEnable: eventResult.feedbackEnable,
                isFeedbackAnonymous: eventResult.isFeedbackAnonymous,
                feedbackSchedule: eventResult.feedbackSchedule,
                feedbackNotificationChannel: eventResult.feedbackNotificationChannel
              }
            }
          : oldData
      );

      // Warning if feedback form has been sent
      const sendAt = eventResult.feedbackSchedule?.sendAt;
      if (sendAt && isBefore(parseISO(sendAt), new Date())) {
        onWarningResendFeedback();
      }

      handleClose();
    }
  });

  const onSubmit = async (data: QuestionFormValues): Promise<void> => {
    if (!event.id) return;

    const questionData = { ...question, ...data } as Question;

    if (question) {
      await updateQuestionMutation.mutateAsync({
        eventId: event.id,
        question: questionData
      });
    } else {
      await addQuestionMutation.mutateAsync({
        eventId: event.id,
        question: questionData
      });
    }
  };

  const handleDeleteClick = (): void => {
    // If there are feedback responses and we're editing an existing question, show confirmation
    if (question && feedbackSubmitted > 0) {
      setIsDeleteConfirmationOpen(true);
    } else {
      // Otherwise, delete immediately (or just close if it's a new question)
      handleDeleteQuestion();
    }
  };

  const handleDeleteQuestion = (): void => {
    if (question) {
      // Delete existing question
      handleSubmit(
        () => onSubmit({ ...question, isDeleted: true } as QuestionFormValues),
        (errors) => {
          console.error(errors);
        }
      )();
    } else {
      // Just close the sheet if it's a new question
      handleClose();
    }
  };

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      {!question && (
        <SheetHeader className="tw-align-start tw-mb-0 tw-flex-shrink-0 tw-pb-0">
          <div
            data-testid="back-button"
            onClick={onBack}
            className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-text-content-tertiary"
          >
            <Icon name="arrow-left" />
            <span className="tw-text-sm tw-font-normal tw-leading-5">Add question</span>
          </div>
        </SheetHeader>
      )}

      <FormProvider {...methods}>
        <div
          className={cn('tw-flex tw-flex-grow tw-flex-col tw-gap-4 tw-overflow-auto', {
            'tw-mt-4': !question
          })}
        >
          <QuestionComponent disabled={disabled} />
        </div>

        {/* Footer */}
        <div className="tw-flex tw-items-center tw-justify-between">
          <Button
            data-testid="submit-button"
            variant="primary"
            size="sm"
            className="tw-rounded-xl"
            onClick={handleSubmit(
              (data) => onSubmit(data),
              (errors) => {
                console.error(errors);
              }
            )}
            disabled={!isValid || isSubmitting || disabled}
          >
            {question ? 'Update' : 'Add question'}
          </Button>

          <Button
            data-testid="delete-button"
            variant="destructive"
            size="sm"
            className="tw-bg-transparent tw-text-negative-foreground"
            type="button"
            onClick={handleDeleteClick}
            disabled={disabled || isSubmitting}
          >
            <Icon name="trash-2" size={16} strokeWidth={2.5} />
            <span className="tw-ml-1">Delete</span>
          </Button>
        </div>
      </FormProvider>

      {/* Delete confirmation dialog */}
      <DeleteQuestionConfirmationDialog
        open={isDeleteConfirmationOpen}
        onOpenChange={setIsDeleteConfirmationOpen}
        onConfirm={() => {
          setIsDeleteConfirmationOpen(false);
          handleDeleteQuestion();
        }}
      />
    </div>
  );
};
